import React, { useState, useEffect } from "react";
import { MapPin, Calendar, Clock, Crosshair } from "lucide-react";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";

const GOOGLE_MAPS_API_KEY = "AIzaSyBSXE8zZ-SqMWdDz16hr00Djvkm3mMit28";

const containerStyle = {
  width: '100%',
  height: '200px',
  borderRadius: '12px'
};

const LocationTime = ({ formData, handleInputChange }) => {
  const [currentPosition, setCurrentPosition] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationName, setLocationName] = useState("");
  const [loading, setLoading] = useState(true);

  // Obtener la ubicación actual del usuario
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setCurrentPosition(pos);
          if (!selectedLocation) {
            setSelectedLocation(pos);
            updateLocationData(pos);
          }
          setLoading(false);
        },
        () => {
          // Default a CDMX si no hay geolocalización
          const defaultPos = { lat: 19.4326, lng: -99.1332 };
          setCurrentPosition(defaultPos);
          if (!selectedLocation) {
            setSelectedLocation(defaultPos);
            updateLocationData(defaultPos);
          }
          setLoading(false);
        }
      );
    }
  }, []);

  // Obtener el nombre de la ubicación usando Geocoding API
  const getLocationName = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();
      if (data.results && data.results[0]) {
        return data.results[0].formatted_address;
      }
      return "Unknown location";
    } catch (error) {
      console.error("Error getting location name:", error);
      return "Unknown location";
    }
  };

  // Actualizar los datos de ubicación en el formulario
  const updateLocationData = async (pos) => {
    const name = await getLocationName(pos.lat, pos.lng);
    setLocationName(name);
    
    // Actualizar formData con la nueva ubicación
    handleInputChange({
      target: {
        name: "location",
        value: name
      }
    });
    
    // Actualizar las coordenadas
    handleInputChange({
      target: {
        name: "coordinates",
        value: {
          lat: pos.lat,
          lng: pos.lng
        }
      }
    });
  };

  // Manejar click en el mapa
  const handleMapClick = async (e) => {
    const pos = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    };
    setSelectedLocation(pos);
    await updateLocationData(pos);
  };

  // Centrar en la ubicación actual
  const handleCenterLocation = () => {
    if (currentPosition) {
      setSelectedLocation(currentPosition);
      updateLocationData(currentPosition);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Map and Location */}
      <div className="space-y-4">
        <div className="relative">
          <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={selectedLocation || currentPosition}
              zoom={15}
              onClick={handleMapClick}
            >
              {selectedLocation && (
                <Marker
                  position={selectedLocation}
                  animation={2} // DROP animation
                />
              )}
            </GoogleMap>
          </LoadScript>
          <button
            type="button"
            onClick={handleCenterLocation}
            className="absolute bottom-4 right-4 bg-white p-2 rounded-lg shadow-md hover:bg-gray-50"
          >
            <Crosshair className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm flex items-center gap-3">
          <MapPin className="w-5 h-5 text-gray-400" />
          <input
            type="text"
            name="location"
            placeholder="Location name"
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
            className="flex-1 focus:outline-none"
            readOnly
          />
        </div>
      </div>

      {/* Date & Time */}
      <div className="space-y-4">
        {/* Date */}
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-600">Date</span>
          </div>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleInputChange}
            className="w-full focus:outline-none mt-1 text-gray-700"
            min={new Date().toISOString().split('T')[0]}
          />
        </div>

        {/* Time */}
        <div className="bg-white p-4 rounded-xl shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-600">Time</span>
          </div>
          <input
            type="time"
            name="time"
            value={formData.time}
            onChange={handleInputChange}
            className="w-full focus:outline-none mt-1 text-gray-700"
          />
        </div>
      </div>
    </div>
  );
};

export default LocationTime;